<template>
    <div class="pb-0 px-0 pt-4 md:pt-6 lg:px-6 lg:py-5 xl:px-10 xl:py-8">
        <VueWindowResize v-model="window" v-show="false" />

        <dashboard-status
            :filter_by_status="filter_by_status"
            :tally="tally"
            @filter="filterByStatus"
        />

        <dashboard-filters
            :has_active_filters="has_active_filters"
            :filter_by_broker="filter_by_broker"
            :filter_by_name="filter_by_name"
            :filter_by_address="filter_by_address"
            :filter_by_closing_month="filter_by_closing_month"
            :shared_brokers="shared_brokers"
            @filterByBroker="filterByBroker"
            @filterByName="filterByName"
            @filterByAddress="filterByAddress"
            @filterByClosingMonth="filterByClosingMonth"
            @clearFilters="clearFilters"
        />

        <div class="mt-10 mb-2 flex items-center justify-between">
            <div class="text-sm sm:text-base flex-none">
                {{ filtered_properties.length }} {{ filtered_properties.length === 1 ? $t('Property') : $t('Properties') }}
            </div>

            <div v-if="filtered_properties.length > 1"
                class="pl-6 text-right uppercase text-gray-500 font-semibold text-xs tracking-widest select-none"
            >
                {{ $t('Sort by Closing Date:') }}

                <span v-if="sort_direction === 'desc'"
                    class="text-blue-500 hover:text-blue-700 cursor-pointer"
                    @click="sortBy('asc')"
                >
                    {{ $t('Descending') }}

                    <i class="text-sm fas fa-caret-down"></i>
                </span>

                <span v-else
                    class="text-blue-500 hover:text-blue-700 cursor-pointer"
                    @click="sortBy('desc')"
                >
                    {{ $t('Ascending') }}

                    <i class="text-sm fas fa-caret-up"></i>
                </span>
            </div>
        </div>

        <dashboard-property v-for="property in sorted_properties"
            :key="property.id"
            :property="property"
            :route="property.route || route"
            :detail_route="property.detail_route || (detail_route + property.id)"
            :read_only="read_only"
            :archivable="true"
            :deletable="false"
            :size="window.width > 1170 ? 'normal' : 'small'"
        ></dashboard-property>
    </div>
</template>

<script>
import DashboardStatus from './dashboard-status.vue';
import DashboardFilters from './dashboard-filters.vue';
import DashboardProperty from './dashboard-property.vue';
import { orderBy } from 'lodash';
import VueWindowResize from 'vue-window-resize';

export default {
    components: {
        DashboardStatus,
        DashboardFilters,
        DashboardProperty,
        VueWindowResize,
    },

    props: {
        properties: { default: () => [] },
        route: {},
        detail_route: {},
        read_only: { default: false },
        shared_brokers: {},
    },

    data() {
        return {
            filter_by_status: null,
            filter_by_name: null,
            filter_by_broker: null,
            filter_by_address: null,
            filter_by_closing_month: null,
            sort_direction: 'desc',
            window: {
                width: 0,
                height: 0,
            },
        }
    },

    computed: {
        tally() {
            return {
                green: this.getPropertiesWithStatus('green').length,
                yellow: this.getPropertiesWithStatus('yellow').length,
                red: this.getPropertiesWithStatus('red').length,
            };
        },
        properties_with_dates() {
            return this.properties.map(property => {
                let closing_date = property.closing.scheduled_closing_date;

                property.closing_date_text = closing_date
                    ? dayjs(closing_date).format('MMMM D, YYYY')
                    : '';
                property.closing_month_number = closing_date
                    ? dayjs(closing_date).format('M')
                    : null;
                property.closing_date = closing_date
                    ? dayjs(closing_date).format('YYYY-MM-DD')
                    : null;

                return property;
            });
        },
        sorted_properties() {
            return orderBy(
                this.filtered_properties,
                [
                    property => property.closing_date || (
                        this.sort_direction == 'asc'
                            ? '9999-99-99'
                            : '0000-00-00'
                    ),
                    property => property.client.name.toLowerCase()
                ],
                [
                    this.sort_direction,
                    'asc'
                ]
            );
        },
        filtered_properties() {
            let properties = this.properties_with_dates;

            if (this.filter_by_status) {
                return this.filtered_properties_by_filters.filter(property => {
                    return property.status.overall === this.filter_by_status;
                });
            }

            return this.filtered_properties_by_filters;
        },
        filtered_properties_by_filters() {
            let properties = this.properties_with_dates;

            if (this.filter_by_broker) {
                properties = properties.filter(property =>
                    property?.broker?.id == this.filter_by_broker
                );
            }

            if (this.filter_by_name) {
                properties = properties.filter(property => {
                    if (! property.client.name) {
                        return;
                    }

                    return property.client.name
                        .toLowerCase()
                        .includes(
                            this.filter_by_name.toLowerCase()
                        );
                })
            }

            if (this.filter_by_address) {
                properties = properties.filter(property => {
                    if (! property.address) {
                        return;
                    }

                    return (property.address + ' ' + property.city + ' ' + property.state)
                        .toLowerCase()
                        .includes(
                            this.filter_by_address.toLowerCase()
                        );
                })
            }

            if (this.filter_by_closing_month) {
                properties = properties.filter(property => {
                    if (! property.closing_date) {
                        return;
                    }

                    if (parseInt(this.filter_by_closing_month)) {
                        return property.closing_month_number
                            == this.filter_by_closing_month;
                    } else {
                        return property.closing_date_text
                            .toLowerCase()
                            .startsWith(
                                this.filter_by_closing_month.toLowerCase()
                            );
                    }
                })
            }

            return properties;
        },
        has_active_filters() {
            return this.filter_by_status
                || this.filter_by_name
                || this.filter_by_address
                || this.filter_by_closing_month;
        },
    },

    methods: {
        filterByStatus(val) {
            this.filter_by_status = this.filter_by_status == val ? null : val;
        },
        filterByBroker(val) {
            this.filter_by_broker = val;
        },
        filterByName(val) {
            this.filter_by_name = val;
        },
        filterByAddress(val) {
            this.filter_by_address = val;
        },
        filterByClosingMonth(val) {
            this.filter_by_closing_month = val;
        },
        clearFilters() {
            this.filter_by_status = null;
            this.filter_by_broker = null;
            this.filter_by_name = null;
            this.filter_by_address = null;
            this.filter_by_closing_month = null;
        },
        sortBy(direction) {
            this.sort_direction = direction;
        },
        getPropertiesWithStatus(color) {
            return this.filtered_properties_by_filters
                .filter(property => property.status.overall == color);
        },
    },
}
</script>
